// File: src/pages/ProductsPage.js (Updated for multi-user JWT support)

import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { FaPlus, FaEdit, FaTrash, FaImage } from "react-icons/fa";
import toast from 'react-hot-toast';
import "./ProductsPage.css";
import SideNav from "../components/SideNav";
import useLicenseCheck from "../hooks/useLicenseCheck";

const POLLING_INTERVAL = 10000;

// Reusable currency formatter function for GHS
const formatCurrency = (amount) => {
  const numericAmount = Number(amount) || 0;
  return `GH₵ ${numericAmount.toFixed(2)}`;
};

const ProductsPage = () => {
useLicenseCheck();

  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/pharmacy/api";
  const IMAGE_BASE_URL = "https://installmentzone.com/pharmacy/";

  // Helper to get token
  const getToken = () => localStorage.getItem("token");

  // Fetch products with JWT token
  useEffect(() => {
    const fetchProducts = async (isInitialLoad = false) => {
      if (isInitialLoad) setLoading(true);
      const token = getToken();
      if (!token) {
        setError("You are not logged in.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/get_products.php?limit=1000`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.status === "success" && Array.isArray(response.data.data)) {
          setProducts(response.data.data);
          setError("");
        } else if (isInitialLoad) {
          setError(response.data.message || "No products available.");
        }
      } catch (err) {
        console.error("Could not load products:", err);
        if (isInitialLoad) setError("Failed to fetch products.");
      } finally {
        if (isInitialLoad) setLoading(false);
      }
    };

    fetchProducts(true);
    const intervalId = setInterval(() => fetchProducts(false), POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [API_BASE_URL]);

  // Add product handler (navigate to Add page)
  const handleAddProduct = () => navigate('/products/add');

  // Edit product handler (navigate to Edit page)
  const handleEdit = (id) => navigate(`/products/edit/${id}`);

  // Delete product handler (sends token)
  const handleDelete = (id, productName) => {
    const token = getToken();
    if (!token) {
      toast.error("You are not logged in.");
      return;
    }

    const deletePromise = axios.post(
      `${API_BASE_URL}/delete_product.php`,
      { id },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    toast.promise(
      deletePromise,
      {
        loading: `Deleting "${productName}"...`,
        success: (response) => {
          if (response.data.status === 'success') {
            setProducts(currentProducts => currentProducts.filter(p => p.id !== id));
            return `"${productName}" deleted successfully!`;
          } else {
            throw new Error(response.data.message || 'Failed to delete on server.');
          }
        },
        error: (err) => `Could not delete: ${err.toString()}`,
      },
      { style: { minWidth: '250px' } }
    );
  };

  return (
    <div className="products-page-container">
      <SideNav activePage="Products" />
      <main className="products-main-content">
        <header className="page-header">
          <h1>Product Inventory</h1>
          <div className="header-actions">
            <button onClick={handleAddProduct} className="add-product-btn">
              <FaPlus />
              Add New Product
            </button>
          </div>
        </header>

        <div className="content-card">
          {loading ? (
            <p className="centered-message">Loading products...</p>
          ) : error ? (
            <p className="centered-message error-message">{error}</p>
          ) : (
            <table className="products-table">
              <thead>
                <tr>
                  <th className="image-col">Image</th>
                  <th>Product Name</th>
                  <th>SKU</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Expiry Date</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.length > 0 ? (
                  products.map((product) => (
                    <tr key={product.id}>
                      <td className="image-col">
                        {product.image_url ? (
                          <img 
                            src={`${IMAGE_BASE_URL}${product.image_url}`} 
                            alt={product.name}
                            className="product-image-thumbnail"
                          />
                        ) : (
                          <div className="product-image-thumbnail" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <FaImage color="#cbd5e1" size={24}/>
                          </div>
                        )}
                      </td>
                      <td>{product.name}</td>
                      <td>{product.sku || 'N/A'}</td>
                      <td>{product.category || 'N/A'}</td>
                      <td className="price-col">{formatCurrency(product.price)}</td>
                      <td className="quantity-col">{product.quantity}</td>
                      <td>{product.expiry_date || 'N/A'}</td>
                      <td className="actions-col">
                        <button
                          onClick={() => handleEdit(product.id)}
                          className="action-btn edit-btn"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id, product.name)}
                          className="action-btn delete-btn"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="centered-message">
                      No products found. Add one to get started!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProductsPage;